/* eslint-disable no-param-reassign */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import ToFixedValue from '../../../shared-functions/ToFixedValue';

interface StationValues {
  _id: string;
  name: string;
  [key: string]: any;
}

interface MetricConfig {
  label: string;
  key: keyof StationValues;
}

interface StationCardProperties {
  stations: StationValues[];
  metrics: MetricConfig[];
  heading: string;
  differenceOnly?: boolean;
  isLoading?: boolean; // <-- ADDED THIS
}

/**
 * StationCard
 */
function StationCard({
  stations,
  metrics,
  heading,
  differenceOnly,
  isLoading = false, // <-- DEFAULT VALUE
}: StationCardProperties) {
  const { t } = useTranslation('sidePanel');
  const comparisonReference = useRef<HTMLDivElement>(null);
  const stableHeading = useRef(heading);

  const referenceStation = stations?.[0];

  /** ------------ Equalize Row Heights ------------ */
  useEffect(() => {
    const container = comparisonReference.current;
    if (!container) return;

    // Reset
    container.querySelectorAll('.row').forEach((row) => {
      (row as HTMLElement).style.height = 'auto';
    });

    // Loop metrics
    metrics.forEach((_, metricIndex) => {
      const rows = Array.from(
        container.querySelectorAll(`.station-card .row:nth-child(${metricIndex + 2})`),
      ) as HTMLElement[];

      if (rows.length > 0) {
        const maxHeight = Math.max(...rows.map((row) => row.offsetHeight));
        rows.forEach((row) => {
          row.style.height = `${maxHeight}px`;
        });
      }
    });
  }, [stations, metrics]); // <-- will refresh whenever station ID changes too

  return (
    <div
      ref={comparisonReference}
      className="station-comparison"
      style={{ '--metric-count': metrics.length } as React.CSSProperties}>
      {/* ---------- Metric Column ---------- */}
      <div className="station-card header">
        <div className="title">{stableHeading.current}</div>

        {metrics.map((metric) => (
          <div key={metric.key as string} className="row metric-label">
            {metric.label}
          </div>
        ))}
      </div>

      {/* ---------- Stations Data or Loader ---------- */}
      {isLoading ? (
        <div className="station-comparison-list">
          <center className="loading">
            <BatteryLoader />
          </center>
        </div>
      ) : (
        stations.map((station, index) => {
          return (
            <div key={station._id} className="station-card body">
              <div className="title">{station.name ? t(station.name) : station.name}</div>

              {metrics.map((metric) => {
                const isDifferent =
                  differenceOnly &&
                  index > 0 &&
                  station[metric.key] !== referenceStation?.[metric.key];

                return (
                  <div
                    key={metric.key as string}
                    className={`row metric-value ${isDifferent ? 'different' : ''}`}>
                    {typeof station[metric.key] === 'number' && !Number.isInteger(station[metric.key])
                      ? ToFixedValue(station[metric.key])
                      : station[metric.key] ?? '-'}

                    {index < stations.length - 1 && <div className="border-right" />}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}

StationCard.defaultProps = {
  differenceOnly: false,
  isLoading: false,
};

export default StationCard;
